import { useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Hash, Lock, ChevronDown, ChevronRight, Loader2, Moon, Sun, Timer, Send, BellRing } from 'lucide-react';
import { adminLookupAPI } from '../../../services/api';

const EVENT_LABELS = {
  created: 'New ticket',
  status_changed: 'Status changed',
  customer_replied: 'Customer replied',
  priority_changed: 'Priority changed',
  assigned: 'Assigned / reassigned',
  returned: 'Back in the queue (open, never carded here)',
  reopened: 'Reopened',
  escalated: "Escalated by a co-managed client's IT team",
};

// The events that can come back out of a thread as their own message.
const RESURFACE_EVENTS = ['customer_replied', 'reopened', 'returned', 'escalated'];

const NOTIFY_LABELS = {
  thread_broadcast: 'Update the card, reply in its thread, and show the reply in the channel (notifies phones)',
  thread: 'Update the card and reply in its thread only (quiet)',
  channel: 'Update the card and post a separate channel message',
};

const PLATFORM_LABELS = { slack: 'Slack', gchat: 'Google Chat', teams: 'Microsoft Teams' };

const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

function useBoardTypes(boardId) {
  return useQuery({
    queryKey: ['board-types', boardId],
    queryFn: () => adminLookupAPI.boardTypes(boardId),
    enabled: !!boardId,
    select: (resp) => resp.data?.items || resp.data || [],
  });
}

function useBoardStatuses(boardId) {
  return useQuery({
    queryKey: ['board-statuses', boardId],
    queryFn: () => adminLookupAPI.boardStatuses(boardId),
    enabled: !!boardId,
    select: (resp) => (resp.data?.items || []).filter((s) => !s.inactiveFlag),
  });
}

const toggleId = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

function Switch({ checked, onChange, label }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
          checked ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'
        }`}
      >
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
      {label}
    </label>
  );
}

function DayPicker({ days, onChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {DAYS.map((d) => {
        const on = days.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            onClick={() => onChange(toggleId(days, d.value))}
            className={`rounded-md border px-2 py-1 text-xs ${
              on
                ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                : 'border-neutral-200 text-neutral-500 dark:border-neutral-600 dark:text-neutral-400'
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function ChannelSelect({ value, channels, onChange, placeholder, disabled, platform }) {
  // Only Slack marks private channels; a Google Chat space and a Teams
  // channel both read fine as a bare name.
  const prefix = (c) => (platform === 'slack' ? (c.isPrivate ? '🔒 ' : '#') : '');
  return (
    <select
      className="input"
      value={value || ''}
      disabled={disabled}
      onChange={(e) => {
        const ch = channels.find((c) => c.id === e.target.value);
        onChange(ch ? { id: ch.id, name: ch.name } : null);
      }}
    >
      <option value="">{placeholder}</option>
      {channels.map((c) => (
        <option key={c.id} value={c.id}>
          {prefix(c)}
          {c.name}
        </option>
      ))}
    </select>
  );
}

function Section({ icon: Icon, title, badge, open, onToggle, children }) {
  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
          {open ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
          {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
          {title}
        </span>
        {badge && <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">{badge}</span>}
      </button>
      {open && <div className="space-y-4 border-t border-neutral-200 px-4 py-4 dark:border-neutral-700">{children}</div>}
    </div>
  );
}

function CompanyFilter({ selected, onChange }) {
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebounce(term, 300);
  const { data: results = [], isFetching } = useQuery({
    queryKey: ['ta-company-search', debouncedTerm],
    queryFn: () => adminLookupAPI.companies(debouncedTerm),
    enabled: debouncedTerm.trim().length >= 2,
    select: (resp) => resp.data?.items || resp.data || [],
  });

  // Names are only known for companies found through a search, so a chip for
  // an id saved earlier falls back to the id rather than showing nothing.
  const [names, setNames] = useState({});
  const add = (c) => {
    setNames((n) => ({ ...n, [c.id]: c.name || c.identifier }));
    if (!selected.includes(c.id)) onChange([...selected, c.id]);
    setTerm('');
  };

  return (
    <div>
      <p className="mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">Only these companies</p>
      <p className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">Leave empty to alert on every company.</p>
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selected.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200">
              {names[id] || `Company #${id}`}
              <button type="button" className="text-neutral-400 hover:text-red-600" onClick={() => onChange(selected.filter((x) => x !== id))}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="input"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search companies to add…"
      />
      {term.trim().length >= 2 && (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
          {isFetching && <p className="px-3 py-2 text-xs text-neutral-500">Searching…</p>}
          {!isFetching && results.length === 0 && <p className="px-3 py-2 text-xs text-neutral-500">No matches.</p>}
          {results.slice(0, 20).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => add(c)}
              className="block w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-700"
            >
              {c.name || c.identifier}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BoardCard({ route, siblingCount = 1, platforms, priorities, eventTypes, notifyModes, defaultTimezone, onSendDigest, digestSending, onChange, onRemove }) {
  const [open, setOpen] = useState({ notes: false, risk: false, filters: false, afterHours: false, digest: false, sla: false, resurface: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const { data: statuses = [], isLoading: statusesLoading } = useBoardStatuses(route.boardId);
  const { data: ticketTypes = [] } = useBoardTypes(route.boardId);

  const platform = route.platform || 'slack';
  const pInfo = platforms[platform] || { connected: false, channels: [], members: [] };
  const connected = pInfo.connected;
  const channels = pInfo.channels || [];
  const members = pInfo.members || [];
  const connectedPlatforms = Object.keys(platforms).filter((k) => platforms[k].connected);
  const channelWord = platform === 'gchat' ? 'space' : 'channel';

  const set = (patch) => onChange({ ...route, ...patch });
  const setEvent = (key, value) => set({ events: { ...route.events, [key]: value } });
  const setFilter = (key, ids) => set({ filters: { ...route.filters, [key]: ids } });
  const ah = route.afterHours || {};
  const setAh = (patch) => set({ afterHours: { ...ah, ...patch } });
  const dg = route.digest || {};
  const setDg = (patch) => set({ digest: { ...dg, ...patch } });
  const sla = route.sla || {};
  const setSla = (patch) => set({ sla: { ...sla, ...patch } });
  // Absent on a route saved before resurfacing existed; the server reads that
  // as the defaults below, so the form shows the same thing.
  const rs = { events: RESURFACE_EVENTS, afterMinutes: 60, ...(route.resurface || {}) };
  const setRs = (patch) => set({ resurface: { ...rs, ...patch } });

  const claimActions = route.claimActions?.length ? route.claimActions : [{ label: 'Take it', statusId: null }];
  const setClaim = (i, patch) => {
    const next = claimActions.map((a, idx) => (idx === i ? { ...a, ...patch } : a));
    set({ claimActions: next });
  };

  const tzHint = `Timezone (blank = ${defaultTimezone || 'portal default'})`;

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
        <div className="font-medium text-neutral-900 dark:text-white">
          {route.boardName || `Board ${route.boardId}`}
          {siblingCount > 1 && (
            <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-normal text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              {PLATFORM_LABELS[platform] || platform} · one of {siblingCount} routes on this board
            </span>
          )}
        </div>
        <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-600 dark:text-neutral-400">
          <Trash2 className="h-3.5 w-3.5" /> Remove
        </button>
      </div>

      <div className="space-y-5 p-4">
        {siblingCount > 1 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <p className="font-medium">This board alerts on more than one platform.</p>
            <p className="mt-1">
              Every ticket gets a card, a thread reply and a set of notifications in <em>each</em> place. That is what
              you want while moving between platforms, or when two teams genuinely work in different apps. The rest of
              the time it is the same alert twice.
            </p>
            <p className="mt-1">
              <strong>Follow is per platform.</strong> Status, claims and notes all live in ConnectWise, so a card on
              either platform shows what happened on the other. Following does not: it is remembered against the chat
              you clicked it in, so following here does not send you anything in the other one.
            </p>
          </div>
        )}
        {/* Platform + channel */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Chat platform</label>
            <select
              className="input"
              value={platform}
              onChange={(e) => set({ platform: e.target.value, channelId: null, channelName: null, afterHours: { ...ah, channelId: null, channelName: null, onCallUserIds: [] }, digest: { ...dg, channelId: null, channelName: null } })}
            >
              {Object.keys(platforms).map((p) => (
                <option key={p} value={p} disabled={!platforms[p].connected && p !== platform}>
                  {PLATFORM_LABELS[p] || p}
                  {!platforms[p].connected ? ' (not connected)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">{platform === 'gchat' ? 'Google Chat space' : platform === 'teams' ? 'Teams channel' : 'Slack channel'}</label>
            {connected ? (
              <ChannelSelect
                value={route.channelId}
                channels={channels}
                platform={platform}
                placeholder={`Select a ${channelWord}…`}
                onChange={(ch) => set({ channelId: ch?.id || null, channelName: ch?.name || null })}
              />
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Connect {PLATFORM_LABELS[platform]} (step 2) to pick a {channelWord}.
                {connectedPlatforms.length > 0 ? ` Or switch this board to ${connectedPlatforms.map((p) => PLATFORM_LABELS[p]).join(' / ')}.` : ''}
              </p>
            )}
            {platform === 'slack' && route.channelId && channels.find((c) => c.id === route.channelId)?.isPrivate && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <Lock className="h-3 w-3" /> Private channel: invite the bot with <code>/invite @Ticket Alerts</code> or posts will fail.
              </p>
            )}
            {platform === 'teams' && connected && channels.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                No channels yet. Add the app to a team in Teams — the portal learns the team and its channels from the
                first event Microsoft sends, which arrives the moment the app is installed.
              </p>
            )}
            {platform === 'gchat' && connected && channels.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                No spaces yet. Open the space in Google Chat and add the app. If the app does not come up when you search for it there, check the
                Chat API Configuration page: <em>Join spaces and group conversations</em> must be ticked, <em>Visibility</em> must list you, and the
                service-account key must come from the same Cloud project as the Chat app.
              </p>
            )}
          </div>
        </div>

        {/* Events */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Alert on</label>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {eventTypes.map((key) => (
              <label key={key} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={route.events?.[key] !== false} onChange={(e) => setEvent(key, e.target.checked)} />
                {EVENT_LABELS[key] || key}
              </label>
            ))}
          </div>
        </div>

        {/* Notify mode */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">When a ticket changes</label>
          <select className="input" value={route.notifyMode || 'thread_broadcast'} onChange={(e) => set({ notifyMode: e.target.value })}>
            {notifyModes.map((m) => (
              <option key={m} value={m}>
                {NOTIFY_LABELS[m] || m}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Each ticket gets one card that is updated in place, so the {channelWord} stays compact. This controls how each change is announced.
            {platform === 'gchat' ? ' Google Chat has no "show in channel" for thread replies; replies always land in the ticket thread, so customer replies, reopens and returning tickets also get a new message in the space (see Resurfacing & mentions).' : ''}
            {platform === 'teams' ? ' Teams threads by conversation, so replies always land under the card and there is no separate broadcast.' : ''} Anyone can press{' '}
            <strong>Follow</strong> on a card to also get a direct message on every change.
          </p>
        </div>

        {/* Claim actions */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">"Take it" buttons</label>
          <p className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">
            Each button assigns the ticket to whoever pressed it and can also set a status (for example "Tier 1 Assigned", whose ConnectWise workflow emails the contact). Up to four buttons.
          </p>
          <div className="space-y-2">
            {claimActions.map((a, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input type="text" className="input w-40" maxLength={40} value={a.label} onChange={(e) => setClaim(i, { label: e.target.value })} placeholder="Button label" />
                <select
                  className="input flex-1 min-w-[12rem]"
                  value={a.statusId || ''}
                  onChange={(e) => {
                    const st = statuses.find((s) => String(s.id) === e.target.value);
                    setClaim(i, { statusId: st?.id || null, statusName: st?.name || null });
                  }}
                >
                  <option value="">Assign only (keep current status)</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      Assign + set status: {s.name}
                    </option>
                  ))}
                </select>
                {claimActions.length > 1 && (
                  <button type="button" onClick={() => set({ claimActions: claimActions.filter((_, idx) => idx !== i) })} className="p-1 text-neutral-400 hover:text-red-600" title="Remove button">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {!a.statusId && (
                  // Said here rather than in a help page nobody reads until
                  // something is wrong: taking a ticket without moving it out
                  // of New leaves the ConnectWise SLA clock running on a
                  // ticket somebody is already working.
                  <p className="w-full text-xs text-amber-700 dark:text-amber-400">
                    Assigns without changing the status, so the SLA clock keeps running. Set a status unless that
                    is what you want.
                  </p>
                )}
              </div>
            ))}
            {statusesLoading && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading board statuses…
              </span>
            )}
            {claimActions.length < 4 && (
              <button type="button" onClick={() => set({ claimActions: [...claimActions, { label: 'Take it', statusId: null, statusName: null }] })} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400">
                <Plus className="h-4 w-4" /> Add button
              </button>
            )}
          </div>
        </div>

        {/* Resurfacing and mentions */}
        <Section icon={BellRing} title="Resurfacing & mentions" badge={rs.events.length ? `${rs.events.length} events` : 'Off'} open={open.resurface} onToggle={() => toggle('resurface')}>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            A reply under a card posted days ago is a thread reply nobody sees. For the events below, an old card gets a
            new message in the {channelWord} as well — what happened, who has it, a link back to the card — and the thread
            reply still goes under the card so the history stays in one place.
            {platform === 'gchat' ? ' Google Chat cannot show a thread reply in the space, so here these events always get a new message, whatever the card’s age.' : ''}
          </p>
          <div>
            <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">Bring these back out of the thread</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {RESURFACE_EVENTS.map((key) => (
                <label key={key} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <input
                    type="checkbox"
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    checked={rs.events.includes(key)}
                    onChange={(e) => setRs({ events: e.target.checked ? [...new Set([...rs.events, key])] : rs.events.filter((k) => k !== key) })}
                  />
                  {EVENT_LABELS[key] || key}
                </label>
              ))}
            </div>
          </div>
          {platform !== 'gchat' && (
            <div className="max-w-xs">
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">When the card is older than (minutes)</label>
              <input
                type="number"
                min={0}
                max={10080}
                className="input"
                value={rs.afterMinutes}
                onChange={(e) => setRs({ afterMinutes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">0 means always. A younger card is still near the bottom of the {channelWord}, so its thread reply is enough.</p>
            </div>
          )}
          <Switch
            checked={route.mentionOwner !== false}
            onChange={(v) => set({ mentionOwner: v })}
            label="@mention whoever owns the ticket when a customer replies, it is reopened, or it comes back"
          />
          {platform === 'teams' ? (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Teams has no channel-wide mention a bot can send, so unassigned tickets are named, not announced to everyone.</p>
          ) : (
            <Switch
              checked={route.mentionWhenUnassigned === true}
              onChange={(v) => set({ mentionWhenUnassigned: v })}
              label={`When nobody owns it, notify everyone in the ${channelWord} (${platform === 'gchat' ? '@all' : '@here'}) — noisy, off by default`}
            />
          )}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            The owner is matched through the Technicians table: the ConnectWise owner first, then the portal assignee. Somebody
            with no linked {PLATFORM_LABELS[platform]} account is named rather than pinged.
          </p>
        </Section>

        {/* After-hours / on-call */}
        <Section icon={Moon} title="After-hours & on-call" badge={ah.enabled ? 'On' : null} open={open.afterHours} onToggle={() => toggle('afterHours')}>
          <Switch checked={ah.enabled === true} onChange={(v) => setAh({ enabled: v })} label="Route alerts differently outside business hours" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">Business days</p>
              <DayPicker days={ah.days || [1, 2, 3, 4, 5]} onChange={(days) => setAh({ days })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Business hours start</label>
              <input type="time" className="input" value={ah.start || '08:00'} onChange={(e) => setAh({ start: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Business hours end</label>
              <input type="time" className="input" value={ah.end || '17:00'} onChange={(e) => setAh({ end: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">{tzHint}</label>
              <input type="text" className="input" placeholder={defaultTimezone || 'America/Los_Angeles'} value={ah.timezone || ''} onChange={(e) => setAh({ timezone: e.target.value || null })} />
            </div>
            <div className="sm:col-span-2">
              <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">After hours, also alert on</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {eventTypes.map((key) => (
                  <label key={key} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                    <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={ah.events?.[key] === true} onChange={(e) => setAh({ events: { ...(ah.events || {}), [key]: e.target.checked } })} />
                    {EVENT_LABELS[key] || key}
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">On-call technicians (direct message)</p>
              {members.length === 0 ? (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Load {PLATFORM_LABELS[platform]} users in step 4 to pick on-call technicians.</p>
              ) : (
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {members.map((m) => (
                    <label key={m.externalUserId} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                      <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={(ah.onCallUserIds || []).includes(m.externalUserId)} onChange={() => setAh({ onCallUserIds: toggleId(ah.onCallUserIds || [], m.externalUserId) })} />
                      {m.displayName || m.email || m.externalUserId}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">After-hours {channelWord} (optional, in addition to the board {channelWord})</label>
              <ChannelSelect value={ah.channelId} channels={channels} platform={platform} disabled={!connected} placeholder="None" onChange={(ch) => setAh({ channelId: ch?.id || null, channelName: ch?.name || null })} />
            </div>
          </div>
        </Section>

        {/* Daily digest */}
        <Section icon={Sun} title="Daily digest" badge={dg.enabled ? 'On' : null} open={open.digest} onToggle={() => toggle('digest')}>
          <Switch checked={dg.enabled === true} onChange={(v) => setDg({ enabled: v })} label="Post a morning summary of open tickets" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Time</label>
              <input type="time" className="input" value={dg.time || '08:00'} onChange={(e) => setDg({ time: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">{tzHint}</label>
              <input type="text" className="input" placeholder={defaultTimezone || 'America/Los_Angeles'} value={dg.timezone || ''} onChange={(e) => setDg({ timezone: e.target.value || null })} />
            </div>
            <div className="sm:col-span-2">
              <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">Days</p>
              <DayPicker days={dg.days || [1, 2, 3, 4, 5]} onChange={(days) => setDg({ days })} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">{platform === 'gchat' ? 'Space' : 'Channel'} (blank = board {channelWord})</label>
              <ChannelSelect value={dg.channelId} channels={channels} platform={platform} disabled={!connected} placeholder={`Board ${channelWord}`} onChange={(ch) => setDg({ channelId: ch?.id || null, channelName: ch?.name || null })} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onSendDigest(route.boardId, route.id)}
              disabled={digestSending || !connected || !(route.channelId || dg.channelId)}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
            >
              {digestSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send digest now
            </button>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">Uses the saved configuration. The scheduled digest posts once per selected day, within two hours of the time above.</span>
          </div>
        </Section>

        {/* SLA warnings */}
        <Section icon={Timer} title="SLA warnings" badge={sla.enabled ? 'On' : null} open={open.sla} onToggle={() => toggle('sla')}>
          <Switch checked={sla.enabled === true} onChange={(v) => setSla({ enabled: v })} label="Warn before ConnectWise SLA deadlines" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Warn this many minutes before the deadline</label>
              <input type="number" min={5} max={1440} className="input" value={sla.warnMinutes ?? 30} onChange={(e) => setSla({ warnMinutes: parseInt(e.target.value, 10) || 30 })} />
            </div>
            <div className="flex flex-col justify-end gap-3">
              <Switch checked={sla.breach !== false} onChange={(v) => setSla({ breach: v })} label="Also alert when a deadline is missed" />
              <Switch checked={sla.notifyOnCall === true} onChange={(v) => setSla({ notifyOnCall: v })} label="Direct-message on-call technicians too" />
            </div>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Uses the respond / resolution-plan / resolve deadlines ConnectWise calculates for each ticket. Tickets are checked every five minutes; each deadline is warned about once, in the ticket's thread.
          </p>
        </Section>

        {/* Notes */}
        <Section title="Notes in the thread" open={open.notes} onToggle={() => toggle('notes')}>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Post a thread reply when someone adds a note in ConnectWise, so the thread carries the conversation and
            not just the status changes. Both are off until you turn them on.
          </p>
          <ul className="space-y-1 rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600 dark:bg-neutral-900/40 dark:text-neutral-400">
            <li>A burst is capped at five, with a count of the rest.</li>
            <li>Notes written from a card here are never posted back, so your own replies are not doubled.</li>
            <li>Quoted email history is trimmed, so a two-word reply arrives as two words.</li>
            <li>Turning this on does not replay the past — it starts from now.</li>
          </ul>
          <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              checked={route.notes?.external === true}
              onChange={(e) => set({ notes: { ...(route.notes || {}), external: e.target.checked } })}
            />
            <span>
              Customer-visible notes
              <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                Discussion and resolution notes, including a customer's own email replies.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              checked={route.notes?.internal === true}
              onChange={(e) => set({ notes: { ...(route.notes || {}), internal: e.target.checked } })}
            />
            <span>
              Internal notes
              <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                Internal analysis notes. Useful on a triage board, noisy on a busy one.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              checked={route.attachments?.announce === true}
              onChange={(e) => set({ attachments: { ...(route.attachments || {}), announce: e.target.checked } })}
            />
            <span>
              Files added in ConnectWise
              <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                Announces the name, size and who added it, with a link. Dropping a file into a ticket thread attaches
                it to that ticket either way; this only controls the other direction.
              </span>
            </span>
          </label>
        </Section>

        {/* Risk */}
        <Section title="Risk watching" open={open.risk} onToggle={() => toggle('risk')}>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Flags tickets that are quietly going wrong, using age, ownership, who is waiting, the SLA clock and how
            much a ticket has been bounced between statuses. No ticket text is read and nothing is sent anywhere. A
            card only carries a warning when two of those go wrong at once; everything else shows up in
            <code> tickets risk</code> instead, so the cards stay quiet.
          </p>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              checked={route.risk?.enabled === true}
              onChange={(e) => set({ risk: { ...(route.risk || {}), enabled: e.target.checked } })}
            />
            Watch this board for tickets going quiet
          </label>
          {route.risk?.enabled && (
            <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Unassigned for (hours)</label>
              <input
                type="number"
                min="1"
                className="input"
                value={route.risk?.unassignedHours ?? ''}
                onChange={(e) => set({ risk: { ...(route.risk || {}), unassignedHours: e.target.value } })}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Nobody has picked it up.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Customer waiting (hours)</label>
              <input
                type="number"
                min="1"
                className="input"
                value={route.risk?.customerWaitingHours ?? ''}
                onChange={(e) => set({ risk: { ...(route.risk || {}), customerWaitingHours: e.target.value } })}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">They replied and we have not.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">No movement for (hours)</label>
              <input
                type="number"
                min="1"
                className="input"
                value={route.risk?.staleHours ?? ''}
                onChange={(e) => set({ risk: { ...(route.risk || {}), staleHours: e.target.value } })}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Owned, but nothing is happening.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Status changes</label>
              <input
                type="number"
                min="1"
                className="input"
                value={route.risk?.churnStatusChanges ?? ''}
                onChange={(e) => set({ risk: { ...(route.risk || {}), churnStatusChanges: e.target.value } })}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Bounced this many times in a week.</p>
            </div>
            </div>
          )}
        </Section>

        {/* Filters */}
        <Section title="Filters (optional)" open={open.filters} onToggle={() => toggle('filters')}>
          <div>
            <p className="mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">Only these priorities</p>
            <p className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">Leave all unchecked to alert on every priority.</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {priorities.map((p) => (
                <label key={p.id} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={(route.filters?.priorityIds || []).includes(p.id)} onChange={() => setFilter('priorityIds', toggleId(route.filters?.priorityIds || [], p.id))} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
          {/* Both of these were honoured by the backend but had no editor, so
              they could only be set by calling the API directly. */}
          {ticketTypes.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">Only these ticket types</p>
              <p className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">Leave all unchecked to alert on every type.</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {ticketTypes.map((t) => (
                  <label key={t.id} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                    <input
                      type="checkbox"
                      className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      checked={(route.filters?.typeIds || []).includes(t.id)}
                      onChange={() => setFilter('typeIds', toggleId(route.filters?.typeIds || [], t.id))}
                    />
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <CompanyFilter
            selected={route.filters?.companyIds || []}
            onChange={(ids) => setFilter('companyIds', ids)}
          />
          <div>
            <p className="mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">Never alert while in these statuses</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {statuses.map((s) => (
                <label key={s.id} className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={(route.excludeStatusIds || []).includes(s.id)} onChange={() => set({ excludeStatusIds: toggleId(route.excludeStatusIds || [], s.id) })} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

/**
 * Per-board routing editor: which boards alert, on which platform, into which
 * channel/space, on which events, with what "Take it" buttons, after-hours
 * routing, digest, SLA and filters.
 *
 * platforms: { slack: { connected, channels, members }, gchat: { connected, channels, members } }
 */
export default function BoardRouteEditor({ routes, availableBoards, platforms, priorities, eventTypes, notifyModes, defaultTimezone, onSendDigest, digestSendingRouteKey, onChange }) {
  const [pendingBoard, setPendingBoard] = useState('');
  // Every board stays on offer, including ones already routed. A board can be
  // routed more than once — to Slack and to Teams during a migration, or to
  // two teams who each live somewhere different — so "already added" is no
  // longer a reason to hide it. The count beside the name says when a board
  // already has routes, since adding a second one should be deliberate.
  const routeCounts = routes.reduce((acc, r) => ({ ...acc, [r.boardId]: (acc[r.boardId] || 0) + 1 }), {});
  const defaultPlatform = Object.keys(platforms).find((p) => platforms[p].connected) || 'slack';

  const addBoard = () => {
    const board = availableBoards.find((b) => String(b.id) === pendingBoard);
    if (!board) return;
    const events = {};
    for (const key of eventTypes) events[key] = true;
    onChange([
      ...routes,
      {
        // A route's own identity. boardId used to serve as one, which is
        // exactly what stopped being true: two routes on one board would have
        // shared a React key, and editing or removing either would have hit
        // both. Minted here so that changing the channel later renames the
        // destination rather than the route.
        id: (crypto.randomUUID?.() || `r${Date.now()}${Math.random().toString(36).slice(2, 8)}`),
        boardId: board.id,
        boardName: board.name,
        platform: defaultPlatform,
        channelId: null,
        channelName: null,
        events,
        notifyMode: 'thread_broadcast',
        notes: { external: false, internal: false },
        attachments: { announce: false },
        risk: { enabled: false },
        filters: { priorityIds: [], companyIds: [], typeIds: [] },
        excludeStatusIds: [],
        claimActions: [{ label: 'Take it', statusId: null, statusName: null }],
        afterHours: { enabled: false, timezone: null, days: [1, 2, 3, 4, 5], start: '08:00', end: '17:00', onCallUserIds: [], channelId: null, channelName: null, events: { created: true, customer_replied: true, priority_changed: true, status_changed: false, assigned: false } },
        digest: { enabled: false, time: '08:00', timezone: null, days: [1, 2, 3, 4, 5], channelId: null, channelName: null },
        sla: { enabled: false, warnMinutes: 30, breach: true, notifyOnCall: false },
      },
    ]);
    setPendingBoard('');
  };

  return (
    <div className="space-y-4">
      {routes.length === 0 && <p className="text-sm text-neutral-500 dark:text-neutral-400">No boards yet. Add the boards whose tickets should alert your chat platform.</p>}
      {routes.map((route, idx) => (
        <BoardCard
          key={route.id || route.boardId}
          route={route}
          siblingCount={routeCounts[route.boardId] || 1}
          platforms={platforms}
          priorities={priorities}
          eventTypes={eventTypes}
          notifyModes={notifyModes}
          defaultTimezone={defaultTimezone}
          onSendDigest={onSendDigest}
          digestSending={digestSendingRouteKey === (route.id || route.boardId)}
          onChange={(next) => onChange(routes.map((r, i) => (i === idx ? next : r)))}
          onRemove={() => onChange(routes.filter((r, i) => i !== idx))}
        />
      ))}

      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        A board can be added more than once, to alert on two platforms at the same time — useful while moving between
        them, or when two teams work in different apps. Every ticket then arrives in both places.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Hash className="h-4 w-4 text-neutral-400" />
        <select className="input w-64" value={pendingBoard} onChange={(e) => setPendingBoard(e.target.value)}>
          <option value="">Add a board…</option>
          {availableBoards.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
              {routeCounts[b.id] ? ` — already routed ${routeCounts[b.id]}×` : ''}
            </option>
          ))}
        </select>
        <button type="button" onClick={addBoard} disabled={!pendingBoard} className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700">
          <Plus className="h-4 w-4" /> Add board
        </button>
      </div>
    </div>
  );
}
